import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { exportOtsExcel } from '../../api/client'
import PlanillaFiltros from '../unidades/PlanillaFiltros'
import { EncabezadoFijo } from '../unidades/PlanillaColumnas'
import { ordenar, siguienteOrden } from '../unidades/ordenLogica'
import { aplicarFiltros, filtroCompleto } from '../unidades/filtrosLogica'
import { formatFecha } from '../unidades/tipos'
import { AsaAncho } from '../AnchoColumnas'
import { useAnchos } from '../useAnchos'
import { CARRILES, diasDesde, tituloOt } from './otLogica'
import { useArrastreArchivos, procesarPdfs, esPdf, conArchivos } from './otPdfs'

// Operaciones > Órdenes de trabajo, as a list laid out like the monday board
// it replaces: one group per stage (collapsible, its colour down the left
// edge), status cells filled with their colour, people as avatars, and a
// summary under each group. Read-only: a row opens the OT's panel. Filters
// by column, sorting by header, column widths by dragging (shared by every
// group), and the ticked rows to Excel.

// monday's palette, so the board looks like the one people know.
const C = {
  gris: '#c4c4c4', amarillo: '#ffcb00', naranjo: '#fdab3d', rojo: '#e2445c', azul: '#579bfc',
  morado: '#a25ddc', azulOsc: '#0086c0', pasto: '#9cd326', verde: '#00c875', grafito: '#808080',
  critica: '#333333', alta: '#401694', media: '#5559df', baja: '#579bfc', celeste: '#66ccff', salmon: '#ff7575',
}
const COLOR_ESTADO = {
  borrador: C.gris, solicitada: C.amarillo, propuesta: C.naranjo, devuelta: C.rojo, planificada: C.azul,
  liberada: C.morado, en_ejecucion: C.azulOsc, terminada: C.pasto, cerrada: C.verde, cancelada: C.grafito,
}
const COLOR_SEMAFORO = { verde: C.verde, amarillo: C.naranjo, rojo: C.rojo }
const COLOR_PRIORIDAD = { critica: C.critica, alta: C.alta, media: C.media, baja: C.baja }
const COLOR_ORIGEN = { proyecto: C.celeste, reposicion: C.salmon, mantenimiento: '#037f4c' }
const COLOR_HITO = { hecho: C.verde, en_cola: C.naranjo, rechazado: C.rojo }
// The confirmations the monday board tracked by hand, in the order they
// happen (server: ot_motor.hitos).
const HITOS = ['of_liberada', 'kit_entregado', 'emision', 'recibido_conforme', 'terminada', 'recibo', 'cierre_of']

// Groups: drafts first (only one's own come), then the stages of the board.
const GRUPOS = [{ id: 'borradores', estados: ['borrador'] }, ...CARRILES]
const colorGrupo = (g) => COLOR_ESTADO[g.estados[0]]

const ANCHOS = {
  nombre: 340, estado: 150, semaforo: 140, prioridad: 120, folio_of: 110, articulo_codigo: 140,
  proyecto_txt: 170, unidad_txt: 150, origen_txt: 120, grupo_txt: 130, responsable_txt: 150, solicitante_txt: 150,
  cantidad: 90, fecha_requerida: 120, fecha_compromiso: 130, dias: 90,
  ...Object.fromEntries(HITOS.map(h => [`hito_${h}`, 118])),
}
const CHECK = 36            // the checkbox column
const FRANJA = 6            // the group's colour strip

const iniciales = (n) => (n || '?').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
const leerVerHitos = () => { try { return localStorage.getItem('ot_lista_ver_hitos') !== '0' } catch { return true } }
const leerCerrados = () => { try { return JSON.parse(localStorage.getItem('ot_lista_grupos_cerrados') || '[]') } catch { return [] } }

// puedeSoltarOf: the user runs the SAP queue -- OF PDFs dropped on the list
// release the OTs they match exactly (otPdfs.js); onActualizar reloads.
export default function ListaOt({ ots, onAbrir, puedeSoltarOf = false, onActualizar }) {
  const { t } = useTranslation(['operations', 'engineering'])
  const [filtros, setFiltros] = useState([])
  const [verFiltros, setVerFiltros] = useState(false)
  const [orden, setOrden] = useState(null)
  const [seleccion, setSeleccion] = useState(() => new Set())
  const [exportando, setExportando] = useState(false)
  const [error, setError] = useState('')
  const [cerrados, setCerrados] = useState(leerCerrados)
  const [verHitos, setVerHitos] = useState(leerVerHitos)
  const alternarHitos = () => setVerHitos(v => {
    try { localStorage.setItem('ot_lista_ver_hitos', v ? '0' : '1') } catch { /* private mode */ }
    return !v
  })
  const { anchos, arrastre, reiniciar } = useAnchos('ot_lista_anchos', ANCHOS)

  // Columns: what each one shows, how it filters and sorts (as text).
  const columnas = useMemo(() => {
    const op = (pref, xs) => xs.map(x => t(`ot.${pref}.${x}`))
    return [
      { id: 'nombre', tipo: 'texto', nombre: t('ot.lista.elemento') },
      { id: 'folio_of', tipo: 'texto', nombre: t('ot.lista.folio'), celda: 'mono' },
      { id: 'estado_txt', ancho: 'estado', tipo: 'estado', nombre: t('ot.lista.estado'), celda: 'estado',
        opciones: op('estados', ['borrador', 'solicitada', 'propuesta', 'devuelta', 'planificada', 'liberada', 'en_ejecucion', 'terminada', 'cerrada', 'cancelada']) },
      { id: 'semaforo_txt', ancho: 'semaforo', tipo: 'estado', nombre: t('ot.lista.semaforo'), celda: 'semaforo', opciones: op('leyenda', ['verde', 'amarillo', 'rojo']) },
      { id: 'prioridad_txt', ancho: 'prioridad', tipo: 'estado', nombre: t('ot.campos.prioridad'), celda: 'prioridad', opciones: op('prioridades', ['critica', 'alta', 'media', 'baja']) },
      // The confirmations ("Confirmaciones" shows or hides them).
      ...(verHitos ? HITOS.map(h => ({ id: `hito_${h}`, hito: h, tipo: 'estado', celda: 'hito', grupoHitos: true,
        nombre: t(`ot.hitos.${h}`), opciones: op('hitos.estados', ['hecho', 'en_cola', 'rechazado']) })) : []),
      { id: 'articulo_codigo', tipo: 'texto', nombre: t('ot.lista.codigo'), celda: 'mono' },
      { id: 'proyecto_txt', tipo: 'texto', nombre: t('ot.campos.proyecto') },
      { id: 'unidad_txt', tipo: 'texto', nombre: t('ot.lista.parteUnidad') },
      { id: 'origen_txt', tipo: 'estado', nombre: t('ot.lista.origen'), celda: 'origen', opciones: op('origenes', ['proyecto', 'reposicion']) },
      { id: 'grupo_txt', tipo: 'texto', nombre: t('ot.campos.grupo') },
      { id: 'responsable_txt', tipo: 'texto', nombre: t('ot.campos.responsable'), celda: 'persona' },
      { id: 'solicitante_txt', tipo: 'texto', nombre: t('ot.campos.solicitante'), celda: 'persona' },
      { id: 'cantidad', tipo: 'numero', nombre: t('ot.campos.cantidad'), celda: 'numero', suma: true },
      { id: 'fecha_requerida', tipo: 'fecha', nombre: t('ot.campos.fecha_requerida'), celda: 'fecha' },
      { id: 'fecha_compromiso', tipo: 'fecha', nombre: t('ot.campos.fecha_compromiso'), celda: 'fecha' },
      { id: 'dias', tipo: 'numero', nombre: t('ot.lista.dias'), celda: 'numero' },
    ].map(c => ({ ...c, ancho: c.ancho || c.id, fijo: true }))
  }, [t, verHitos])

  const filas = useMemo(() => ots.map(o => ({
    ...o,
    nombre: `${o.codigo} ${o.lote ? o.lote.codigo + ' ' : ''}${tituloOt(o) || ''}`.trim(),
    unidad_txt: o.parte ? `#${o.parte.numero} · ${o.parte.unidad_codigo || ''}${o.parte.implica_fs ? ' · F/S' : ''}` : null,
    estado_txt: t(`ot.estados.${o.estado}`, { defaultValue: o.estado }),
    semaforo_txt: o.semaforo ? t(`ot.leyenda.${o.semaforo.color}`) : null,
    proyecto_txt: o.proyecto ? `${o.proyecto.name}${o.proyecto.code ? ` (${o.proyecto.code})` : ''}` : null,
    origen_txt: t(`ot.origenes.${o.origen}`, { defaultValue: o.origen }),
    grupo_txt: o.grupo?.nombre || (o.grupo_propuesto ? `${o.grupo_propuesto.nombre} (${t('ot.admision.propuesto')})` : null),
    prioridad_txt: o.prioridad ? t(`ot.prioridades.${o.prioridad}`) : null,
    responsable_txt: o.responsable?.nombre || null,
    solicitante_txt: o.solicitante?.display_name || null,
    dias: diasDesde(o.estado_desde),
    ...Object.fromEntries(HITOS.map(h => [`hito_${h}`, o.hitos?.[h] ? t(`ot.hitos.estados.${o.hitos[h]}`) : null])),
  })), [ots, t])
  const visibles = useMemo(() => ordenar(aplicarFiltros(filas, filtros, columnas), orden, columnas),
    [filas, filtros, columnas, orden])
  const grupos = useMemo(() => GRUPOS.map(g => ({ ...g, filas: visibles.filter(o => g.estados.includes(o.estado)) }))
    .filter(g => g.filas.length), [visibles])
  const activos = filtros.filter(filtroCompleto).length
  const ordenarPor = (campo, dir) => setOrden(o => siguienteOrden(o, campo, dir))

  const marcar = (ids, si) => setSeleccion(s => {
    const n = new Set(s)
    ids.forEach(id => (si ? n.add(id) : n.delete(id)))
    return n
  })
  const seleccionadas = filas.filter(o => seleccion.has(o.id))
  const descargar = async () => {
    setExportando(true); setError('')
    try { await exportOtsExcel(seleccionadas.map(o => o.id)) } catch { setError(t('engineering:unidades.filtros.exportFailed')) }
    setExportando(false)
  }
  const alternar = (id) => setCerrados(cs => {
    const n = cs.includes(id) ? cs.filter(c => c !== id) : [...cs, id]
    try { localStorage.setItem('ot_lista_grupos_cerrados', JSON.stringify(n)) } catch { /* private mode */ }
    return n
  })
  const anchoTotal = CHECK + FRANJA + columnas.reduce((s, c) => s + anchos[c.ancho], 0)

  // OF PDFs dragged in from another window.
  const arrastrando = useArrastreArchivos(puedeSoltarOf)
  const [pdfs, setPdfs] = useState(null)          // { procesando, n } | { ignorados, resultados }
  const esperanOf = filas.filter(o => o.hitos?.of_liberada === 'en_cola').length
  const soltar = async (e) => {
    if (!puedeSoltarOf || !conArchivos(e)) return
    e.preventDefault()
    const lista = Array.from(e.dataTransfer.files || [])
    if (!lista.some(esPdf)) { setPdfs({ ignorados: lista.length, resultados: [] }); return }
    setPdfs({ procesando: true, n: lista.filter(esPdf).length })
    try {
      const r = await procesarPdfs(lista)
      setPdfs(r)
      if (r.resultados.some(x => x.estado === 'liberada')) onActualizar?.()
    } catch {
      setPdfs({ ignorados: 0, resultados: [], fallo: true })
    }
  }

  return (
    <div onDrop={soltar}>
      <div className="mb-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => {
                if (!verFiltros && !filtros.length) setFiltros([{ id: Date.now(), campo: 'nombre', op: 'contains', valor: '' }])
                setVerFiltros(v => !v)
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                activos ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              {t('engineering:unidades.filtros.button')}{activos ? ` · ${activos}` : ''}
            </button>
            <button type="button" onClick={alternarHitos} aria-pressed={verHitos}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                verHitos ? 'border-emerald-600 bg-emerald-50 text-emerald-800' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
              <span aria-hidden>✓</span>{t('ot.lista.confirmaciones')}
            </button>
            <span className="text-xs text-gray-500">{t('ot.lista.mostrando', { n: visibles.length, total: filas.length })}</span>
            {activos > 0 && (
              <button type="button" onClick={() => setFiltros([])} className="text-xs font-medium text-red-600 hover:text-red-700">
                {t('engineering:unidades.filtros.clearAll')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {seleccionadas.length > 0 && (
              <>
                <span className="text-xs text-gray-500">{t('ot.lista.seleccionadas', { count: seleccionadas.length })}</span>
                <button type="button" onClick={() => setSeleccion(new Set())} className="text-xs font-medium text-gray-500 hover:text-gray-900">
                  {t('engineering:unidades.filtros.clearSelection')}
                </button>
              </>
            )}
            <button type="button" onClick={descargar} disabled={!seleccionadas.length || exportando}
              title={seleccionadas.length ? undefined : t('ot.lista.eligePrimero')}
              className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exportando ? t('engineering:unidades.filtros.exporting') : t('engineering:unidades.filtros.export')}
            </button>
          </div>
        </div>
        {verFiltros && <PlanillaFiltros columnas={columnas} filtros={filtros} onChange={setFiltros} />}
      </div>
      {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
      {arrastrando && (
        <div className="mb-4 rounded-xl border-2 border-dashed border-indigo-400 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          {esperanOf ? t('ot.lista.pdf.guia', { count: esperanOf }) : t('ot.lista.pdf.sinDestinos')}
        </div>
      )}
      {pdfs && <ResultadoPdfs r={pdfs} t={t} onAbrir={onAbrir} onCerrar={() => setPdfs(null)} />}

      {!grupos.length ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-400">
          {filas.length ? t('ot.lista.ninguna') : t('ot.lista.vacia')}
        </p>
      ) : (
        <div className="overflow-x-auto pb-2" data-anchos-propios>
          <div className="space-y-7" style={{ minWidth: anchoTotal }}>
            {grupos.map(g => (
              <Grupo key={g.id} g={g} t={t} columnas={columnas} anchos={anchos} anchoTotal={anchoTotal}
                cerrado={cerrados.includes(g.id)} onAlternar={() => alternar(g.id)}
                seleccion={seleccion} onMarcar={marcar} orden={orden} onOrdenar={ordenarPor}
                onArrastre={arrastre} onReiniciar={reiniciar} onAbrir={onAbrir} resaltarOf={arrastrando} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Grupo({ g, t, columnas, anchos, anchoTotal, cerrado, onAlternar, seleccion, onMarcar, orden, onOrdenar, onArrastre, onReiniciar, onAbrir, resaltarOf }) {
  const color = colorGrupo(g)
  const ids = g.filas.map(o => o.id)
  const todas = ids.every(id => seleccion.has(id))
  const alguna = ids.some(id => seleccion.has(id))
  const titulo = g.id === 'borradores' ? t('ot.lista.borradores') : t(`ot.carriles.${g.id}`)

  const cabeza = (
    <div className="flex items-center gap-2 mb-1.5">
      <button type="button" onClick={onAlternar} aria-expanded={!cerrado}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-gray-100" style={{ color }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform ${cerrado ? '-rotate-90' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
        <span className="text-[17px] font-semibold">{titulo}</span>
      </button>
      <span className="text-xs text-gray-400">{t('ot.lista.nElementos', { count: g.filas.length })}</span>
    </div>
  )
  if (cerrado) {
    return (
      <section>
        {cabeza}
        <button type="button" onClick={onAlternar}
          className="flex w-full items-center gap-3 rounded-md border border-gray-200 bg-white text-left text-sm hover:bg-gray-50"
          style={{ borderLeft: `${FRANJA}px solid ${color}`, minWidth: anchoTotal }}>
          <span className="px-3 py-2 font-medium" style={{ color }}>{titulo}</span>
          <span className="text-xs text-gray-400">{t('ot.lista.nElementos', { count: g.filas.length })}</span>
          <span className="ml-auto mr-3 w-40"><Bateria filas={g.filas} campo="estado" colores={COLOR_ESTADO} /></span>
        </button>
      </section>
    )
  }

  return (
    <section>
      {cabeza}
      <table className="border-separate border-spacing-0 text-[13px]" style={{ tableLayout: 'fixed', width: anchoTotal, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: FRANJA }} />
          <col style={{ width: CHECK }} />
          {columnas.map(c => <col key={c.id} style={{ width: anchos[c.ancho] }} />)}
        </colgroup>
        <thead>
          <tr>
            <th className="sticky left-0 z-20 bg-white p-0" style={{ background: color, borderTopLeftRadius: 6 }} />
            <th className="sticky z-20 border-y border-r border-gray-200 bg-white" style={{ left: FRANJA }}>
              <input type="checkbox" checked={todas} aria-label={t('engineering:unidades.filtros.selectAll')}
                ref={el => { if (el) el.indeterminate = !todas && alguna }} onChange={() => onMarcar(ids, !todas)} className="cursor-pointer align-middle" />
            </th>
            {columnas.map((c, i) => (
              <th key={c.id}
                className={`relative h-9 border-y border-r border-gray-200 bg-white px-2 font-normal text-gray-500 ${
                  i === 0 ? 'sticky z-20 text-left' : 'text-center'} ${i === columnas.length - 1 ? 'rounded-tr-md' : ''}`}
                style={i === 0 ? { left: FRANJA + CHECK } : undefined}>
                <span className={`block truncate ${c.grupoHitos ? 'text-[11px]' : ''}`} title={c.nombre}>
                  <EncabezadoFijo label={c.nombre} campo={c.id} tipo={c.tipo === 'estado' ? 'texto' : c.tipo} orden={orden} onOrdenar={onOrdenar} />
                </span>
                <AsaAncho onArrastre={e => onArrastre(c.ancho, e)} onReiniciar={() => onReiniciar(c.ancho)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {g.filas.map(o => {
            const marcada = seleccion.has(o.id)
            // While OF PDFs are dragged in: the OTs waiting for theirs light up.
            const esperaOf = resaltarOf && o.hitos?.of_liberada === 'en_cola'
            const fondo = esperaOf ? 'bg-indigo-100' : marcada ? 'bg-[#e5f4ff]' : 'bg-white group-hover/fila:bg-[#f5f6f8]'
            return (
              <tr key={o.id} onClick={() => onAbrir(o.id)} className="group/fila cursor-pointer">
                <td className="sticky left-0 z-10 p-0" style={{ background: color }} />
                <td className={`sticky z-10 h-9 border-b border-r border-gray-200 text-center ${fondo}`} style={{ left: FRANJA }}
                  onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={marcada} onChange={() => onMarcar([o.id], !marcada)} className="cursor-pointer align-middle" />
                </td>
                {columnas.map((c, i) => (
                  <Celda key={c.id} c={c} o={o} t={t} primera={i === 0} fondo={fondo} />
                ))}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="p-0" style={{ background: color, borderBottomLeftRadius: 6 }} />
            <td className="border-b border-r border-gray-200 bg-white" />
            {columnas.map((c, i) => (
              <td key={c.id} className={`h-9 border-b border-r border-gray-200 bg-white px-2 ${i === columnas.length - 1 ? 'rounded-br-md' : ''}`}>
                <Resumen c={c} filas={g.filas} t={t} />
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  )
}

function Celda({ c, o, t, primera, fondo }) {
  const base = `h-9 border-b border-r border-gray-200 ${fondo}`
  if (primera) {
    return (
      <td className={`${base} sticky z-10 px-2`} style={{ left: FRANJA + CHECK }}>
        <span className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] text-gray-500 shrink-0">{o.codigo}</span>
          {o.lote && <span className="shrink-0 px-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold">{o.lote.codigo}</span>}
          <span className="truncate text-gray-900">{tituloOt(o) || '—'}</span>
          {o.n_mensajes > 0 && (
            <span className="ml-auto shrink-0 inline-flex items-center gap-0.5 rounded-full bg-[#0073ea] px-1.5 text-[10px] font-medium text-white"
              title={t('ot.chat.titulo')}>💬 {o.n_mensajes}</span>
          )}
        </span>
      </td>
    )
  }
  const lleno = (color, texto, titulo = texto) => (
    <td className={`${base} p-0`}>
      {texto
        ? <span className="flex h-9 items-center justify-center px-1.5 text-white" style={{ background: color }} title={titulo}>
            <span className="truncate">{texto}</span>
          </span>
        : <span className="flex h-9 items-center justify-center bg-[#c4c4c4]/40" />}
    </td>
  )
  switch (c.celda) {
    case 'estado':
      return lleno(COLOR_ESTADO[o.estado], o.subestado ? `${o.estado_txt} · ${t(`ot.subestados.${o.subestado}`, { defaultValue: o.subestado })}` : o.estado_txt)
    case 'semaforo':
      return lleno(COLOR_SEMAFORO[o.semaforo?.color], o.semaforo_txt,
        o.semaforo ? t(`ot.semaforo.${o.semaforo.motivo}`, { defaultValue: o.semaforo_txt }) : null)
    case 'prioridad':
      return lleno(COLOR_PRIORIDAD[o.prioridad], o.prioridad_txt)
    case 'origen':
      return lleno(COLOR_ORIGEN[o.origen], o.origen_txt)
    case 'hito': {
      if (o.parte) return <td className={`${base} text-center text-gray-300`}>—</td>   // nothing asked of SAP
      const v = o.hitos?.[c.hito]
      return lleno(COLOR_HITO[v], v ? (v === 'hecho' ? '✓' : o[c.id]) : null, v ? `${c.nombre}: ${o[c.id]}` : c.nombre)
    }
    case 'persona': {
      const n = o[c.id]
      return (
        <td className={`${base} px-2`}>
          {n ? (
            <span className="flex items-center gap-1.5 min-w-0" title={n}>
              <span className="h-6 w-6 shrink-0 rounded-full bg-[#579bfc] text-[10px] font-semibold text-white flex items-center justify-center">{iniciales(n)}</span>
              <span className="truncate text-gray-700">{n}</span>
            </span>
          ) : <span className="flex justify-center"><span className="h-6 w-6 rounded-full border border-dashed border-gray-300" /></span>}
        </td>
      )
    }
    case 'fecha':
      return <td className={`${base} px-2 text-center text-gray-700`}>{o[c.id] ? formatFecha(o[c.id]) : ''}</td>
    case 'numero':
      return <td className={`${base} px-2 text-center tabular-nums text-gray-700`}>{o[c.id] ?? ''}</td>
    case 'mono':
      return <td className={`${base} px-2 text-center font-mono text-xs text-gray-700 truncate`}>{o[c.id] || ''}</td>
    default:
      return <td className={`${base} px-2 text-center text-gray-700 truncate`} title={o[c.id] || undefined}>{o[c.id] || ''}</td>
  }
}

// Under each group, as on monday: how its status columns split (a battery),
// and the sum of a number column.
function Resumen({ c, filas, t }) {
  if (c.celda === 'estado') return <Bateria filas={filas} campo="estado" colores={COLOR_ESTADO} />
  if (c.celda === 'semaforo') return <Bateria filas={filas} valor={o => o.semaforo?.color} colores={COLOR_SEMAFORO} />
  if (c.celda === 'prioridad') return <Bateria filas={filas} campo="prioridad" colores={COLOR_PRIORIDAD} />
  if (c.celda === 'origen') return <Bateria filas={filas} campo="origen" colores={COLOR_ORIGEN} />
  if (c.celda === 'hito') return <Bateria filas={filas} valor={o => o.hitos?.[c.hito]} colores={COLOR_HITO} />
  if (c.suma) {
    const s = filas.reduce((a, o) => a + (Number(o[c.id]) || 0), 0)
    return <span className="block text-center text-xs font-semibold tabular-nums text-gray-700">{s}<span className="block text-[10px] font-normal text-gray-400">{t('ot.lista.suma')}</span></span>
  }
  return null
}

function Bateria({ filas, campo, valor, colores }) {
  const cuenta = {}
  filas.forEach(o => { const v = valor ? valor(o) : o[campo]; cuenta[v ?? ''] = (cuenta[v ?? ''] || 0) + 1 })
  const total = filas.length || 1
  return (
    <span className="flex h-5 w-full overflow-hidden rounded-sm">
      {Object.entries(cuenta).map(([v, n]) => (
        <span key={v} style={{ width: `${(n / total) * 100}%`, background: colores[v] || C.gris }} title={`${n}`} />
      ))}
    </span>
  )
}

// What became of the OF PDFs dropped on the list.
function ResultadoPdfs({ r, t, onAbrir, onCerrar }) {
  if (r.procesando) {
    return <p className="mb-4 rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm text-gray-600">{t('ot.lista.pdf.procesando', { count: r.n })}</p>
  }
  const TONO = { liberada: 'bg-emerald-50 text-emerald-900', ambiguo: 'bg-indigo-50 text-indigo-900', de_cancelada: 'bg-amber-50 text-amber-900', sin_coincidencia: 'bg-amber-50 text-amber-900',
    error: 'bg-rose-50 text-rose-800', sin_destinos: 'bg-gray-50 text-gray-700' }
  const liberadas = r.resultados.filter(x => x.estado === 'liberada').length
  return (
    <div className="mb-4 rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-medium text-gray-900">{t('ot.lista.pdf.titulo', { count: r.resultados.length, liberadas })}</span>
        {r.ignorados > 0 && <span className="text-xs text-amber-700">{t('ot.lista.pdf.ignorados', { count: r.ignorados })}</span>}
        {r.fallo && <span className="text-xs text-rose-700">{t('ot.lista.pdf.fallo')}</span>}
        <button type="button" onClick={onCerrar} className="ml-auto text-xs text-gray-500 hover:text-gray-900">{t('ot.lista.pdf.cerrar')}</button>
      </div>
      <ul className="space-y-1">
        {r.resultados.map((x, n) => (
          <li key={`${x.archivo}-${n}`} className={`flex flex-wrap items-center gap-2 rounded-md px-2.5 py-1 ${TONO[x.estado]}`}>
            <span className="truncate max-w-[40ch]">📄 {x.archivo}</span>
            <span className="text-xs">
              {x.estado === 'liberada' && (
                <>✓ {t('ot.lista.pdf.liberada', { folio: x.folio })}{' '}
                  <button type="button" onClick={() => onAbrir(x.otId)} className="font-mono font-semibold underline hover:no-underline">{x.ot}</button></>
              )}
              {x.estado === 'ambiguo' && t('ot.lista.pdf.ambiguo', { lista: x.candidatos.join(', ') })}
              {x.estado === 'de_cancelada' && t('ot.lista.pdf.deCancelada', { ot: x.ot })}
              {x.estado === 'sin_coincidencia' && t('ot.lista.pdf.sinCoincidencia')}
              {x.estado === 'sin_destinos' && t('ot.lista.pdf.sinDestinos')}
              {x.estado === 'error' && t('ot.lista.pdf.error', { ot: x.ot || '—', mensaje: x.mensaje || '' })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
